import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWhatsAppService from '../../services/adminWhatsAppService';
import { WHATSAPP_MESSAGE_SOURCE } from '../../utils/whatsapp';

/** Wait after the last tick before asking the server again, so ticking several rows is one request. */
export const PREVIEW_DEBOUNCE_MS = 400;

/** Adds rows this response has that the roster lacks (by source_id), keeping earlier rows and their order. */
const mergeRoster = (existing, incoming) => {
  const known = new Set(existing.map((r) => r.source_id));
  return [...existing, ...incoming.filter((r) => !known.has(r.source_id))];
};

/**
 * The server's answer for who a campaign would reach (the counts always come from it, never from
 * ticks). Fetches while `active` whenever the request body changes: at once for a new audience,
 * after a short pause when only the ticks changed.
 *
 * `roster` is every sendable person seen for the current audience, ticked or not, because the
 * server drops unticked people from its answer and they must stay in the list to be ticked again.
 * `fresh` means `data` answers the request for the inputs on screen right now.
 *
 * @param {{active: boolean, body: object | null, audienceKey: string}} args
 */
export default function useAudiencePreview({ active, body, audienceKey }) {
  const toast = useToast();
  const requestKey = JSON.stringify(body);
  const [store, setStore] = useState({ requestKey: '', audienceKey: '', data: null, roster: [] });
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (!active || requestKey === 'null' || store.requestKey === requestKey || failure?.requestKey === requestKey) {
      return undefined;
    }
    const request = JSON.parse(requestKey);
    let cancelled = false;
    const run = async () => {
      try {
        const data = await adminWhatsAppService.previewAudience(request);
        if (cancelled) return;
        setStore((prev) => {
          const same = prev.audienceKey === audienceKey;
          const roster = same && request.source !== WHATSAPP_MESSAGE_SOURCE.CSV ? mergeRoster(prev.roster, data.recipients) : data.recipients;
          return { requestKey, audienceKey, data, roster };
        });
      } catch (err) {
        if (cancelled) return;
        setFailure({ requestKey, message: err.message });
        toast.error(err.message);
      }
    };
    const delay = store.audienceKey === audienceKey ? PREVIEW_DEBOUNCE_MS : 0;
    const timer = setTimeout(run, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, requestKey, audienceKey, store, failure, toast]);

  const sameAudience = store.audienceKey === audienceKey;
  const error = failure?.requestKey === requestKey ? failure.message : '';
  const fresh = Boolean(body) && store.requestKey === requestKey;
  return {
    data: sameAudience ? store.data : null,
    roster: sameAudience ? store.roster : [],
    fresh,
    loading: active && Boolean(body) && !fresh && !error,
    error,
    retry: () => setFailure(null),
  };
}
